import { useEffect } from "react";
import Shepherd from "shepherd.js";
import { offset, arrow } from "@floating-ui/dom";

const TOTAL = 4;

function makeText(text, idx) {
  const dots = Array.from({ length: TOTAL }, (_, i) =>
    `<span class="hw-dot${i === idx ? " hw-dot-active" : ""}"></span>`
  ).join("");
  return `<p style="margin:0">${text}</p><div class="hw-dots">${dots}</div>`;
}

const OFFSET = 12;
const EDGE_GAP = 12;
const clampToScreen = (pos, size, screen) => Math.max(EDGE_GAP, Math.min(pos, screen - size - EDGE_GAP));
const hasRoom = (room, size) => room >= size + OFFSET + EDGE_GAP;
// The side of its target a tooltip with no room above or below it fits on, if any.
const sideWithRoom = (target, { width, height }, screenWidth, screenHeight) => {
  if (hasRoom(target.top, height) || hasRoom(screenHeight - target.bottom, height)) return null;
  if (hasRoom(target.left, width)) return "left";
  return hasRoom(screenWidth - target.right, width) ? "right" : null;
};

// Shepherd runs its own flip/shift/arrow first and appends these: 12px between target and
// tooltip; then 12px from the screen edges (Shepherd's shift has no padding, and the offset
// after it pushes a tooltip with no room above its target 12px off the top). That clamp
// uses the layout viewport, not the visual one shift() uses: after a rotation the WebView
// can be zoomed out, and a tooltip kept inside the wider visual viewport holds the zoom.
// On a phone in landscape a tooltip can fit neither above nor below its target, and the
// clamp would put it over the target: it goes beside the target instead, where it fits.
// Finally the arrow is re-aimed at the target, since the clamp may have moved the tooltip.
export const TOOLTIP_PLACEMENT = {
  floatingUIOptions: {
    middleware: [
      offset(OFFSET),
      {
        name: "edgeGap",
        fn: ({ x, y, placement, rects, elements, middlewareData }) => {
          const { clientWidth, clientHeight } = document.documentElement;
          const target = elements?.reference.getBoundingClientRect();
          // Floating UI reruns every middleware for the new placement: moving only once per
          // positioning, it can't bounce against Shepherd's flip.
          const side = target && /^(top|bottom)/.test(placement) && !middlewareData.edgeGap?.side &&
            sideWithRoom(target, rects.floating, clientWidth, clientHeight);
          if (side) return { data: { side }, reset: { placement: side } };
          // x and y are relative to the tooltip's offset parent, which the page may have
          // scrolled: the target's two rects give the screen's origin in those coordinates.
          const dx = target ? rects.reference.x - target.left : 0;
          const dy = target ? rects.reference.y - target.top : 0;
          return {
            x: dx + clampToScreen(x - dx, rects.floating.width, clientWidth),
            y: dy + clampToScreen(y - dy, rects.floating.height, clientHeight),
          };
        },
      },
      {
        name: "arrow",
        fn: async (state) => {
          const element = state.elements.floating.querySelector(".shepherd-arrow");
          if (!element) return {};
          const { data, ...aimed } = await arrow({ element }).fn(state);
          // Middleware data outlives a change of placement: drop the other axis's offset.
          return { ...aimed, data: { x: undefined, y: undefined, ...data } };
        },
      },
    ],
  },
};

const HomeWalkthrough = ({ onDone, onSkip }) => {
  useEffect(() => {
    const tour = new Shepherd.Tour({
      useModalOverlay: true,
      defaultStepOptions: {
        cancelIcon: { enabled: false },
        modalOverlayOpeningRadius: 14,
        modalOverlayOpeningPadding: 8,
        scrollTo: false,
        ...TOOLTIP_PLACEMENT,
      },
    });

    const skipBtn = { text: "Skip", action: () => tour.cancel(), classes: "hw-btn-secondary" };
    const nextBtn = (label = "Next →") => ({ text: label, action: () => tour.next(), classes: "hw-btn-primary" });
    // Home scrolls in phone landscape (the card starts below the fold): bring each step's
    // target into view before Shepherd measures it.
    const inView = (selector) => ({
      "before-show": () => document.querySelector(selector)?.scrollIntoView({ block: "nearest" }),
    });

    tour.addStep({
      id: "welcome",
      title: "Welcome to Hans Wehr",
      // <bdi> isolates each Arabic word; otherwise the bidi algorithm renders the
      // comma-separated words as one reversed run.
      text: makeText("Hans Wehr is a classical Arabic-English <b>roots dictionary</b>. Arabic words are built from 3-letter roots — look up a root like كتب to find <bdi>كاتب</bdi>, <bdi>مكتبة</bdi>, <bdi>كتاب</bdi>, and more.", 0),
      buttons: [skipBtn, nextBtn()],
    });
    tour.addStep({
      id: "search",
      title: "Search a root",
      // Each mapping is an LTR isolate: unisolated, "ص, 7→ح" forms one RTL run and the
      // line reads "S→ح→7 ,ص".
      text: makeText("Type roots in English letters — ktb finds كتب, fel finds فعل. Capitals and numbers map to emphatic sounds: <bdi dir=\"ltr\">S→ص</bdi>, <bdi dir=\"ltr\">7→ح</bdi>.", 1),
      attachTo: { element: "#wt-home-search", on: "bottom" },
      when: inView("#wt-home-search"),
      // A tap would focus the field (the keyboard opens under the tour) or open the Typing
      // Guide beneath the overlay, where it can't be closed. Disabled, taps reach the
      // scroll box behind it, which ignores them.
      canClickTarget: false,
      buttons: [skipBtn, nextBtn()],
    });
    tour.addStep({
      id: "guide",
      title: "Character map",
      text: makeText("Tap ? to see every English letter and number mapped to its Arabic equivalent.", 2),
      attachTo: { element: "#wt-home-guide", on: "bottom" },
      when: inView("#wt-home-guide"),
      // Not canClickTarget: false, as a tap would fall through to the field and focus it;
      // Home ignores taps on ? during the tour instead. The opening is exactly the round
      // 48px button, so no tap in it reaches the field or the Search button next to it.
      modalOverlayOpeningPadding: 0,
      modalOverlayOpeningRadius: 24,
      buttons: [skipBtn, nextBtn()],
    });

    // Card is mobile-only — only add this step if the element is rendered and visible
    const card = document.querySelector("#wt-home-card");
    if (card && card.offsetParent !== null) {
      tour.addStep({
        id: "card",
        title: "Discover a new root",
        text: makeText("Opens a random root. Tap the refresh icon to get another.", 3),
        attachTo: { element: "#wt-home-card", on: "top" },
        when: inView("#wt-home-card"),
        buttons: [skipBtn, nextBtn("Finish")],
      });
    }

    // Swipe left → next, swipe right → back
    let touchStartX = 0;
    let touchStartY = 0;
    const onTouchStart = (e) => {
      touchStartX = e.touches[0].clientX;
      touchStartY = e.touches[0].clientY;
    };
    const onTouchEnd = (e) => {
      const dx = e.changedTouches[0].clientX - touchStartX;
      const dy = e.changedTouches[0].clientY - touchStartY;
      if (Math.abs(dx) > Math.abs(dy) && Math.abs(dx) > 40) {
        if (dx < 0) tour.next();
        else tour.back();
      }
    };
    document.addEventListener("touchstart", onTouchStart, { passive: true });
    document.addEventListener("touchend", onTouchEnd, { passive: true });

    tour.on("cancel", onSkip);
    tour.on("complete", onDone);
    tour.start();

    return () => {
      document.removeEventListener("touchstart", onTouchStart);
      document.removeEventListener("touchend", onTouchEnd);
      tour.off("cancel");
      tour.off("complete");
      try { tour.cancel(); } catch (_) {}
    };
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  return null;
};

export default HomeWalkthrough;
